import { useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Moon, Sun, Command, Search as SearchIcon, LogOut, Settings, ShieldCheck, User as UserIcon, Bug, CircleHelp, Plus, Pencil } from 'lucide-react';
import { Avatar, Logo, Menu, MenuItem, MenuDivider, Popover, Tooltip, useToast } from '@vd/ui';
import { useTheme } from '../../app/theme';
import { useAuth, useCurrentCompany } from '../auth/AuthProvider';
import { useNavigate } from 'react-router-dom';
import { useShortcut } from '../../lib/keyboard';
import { GetExtensionPrompt } from './GetExtensionPrompt';
import { CreateWorkspaceModal } from '../modals/CreateWorkspaceModal';
import { NotificationsBell } from './NotificationsBell';
import styles from './TopBar.module.css';
import { useNavigationConfirmation } from '../../app/NavigationGuard';
import { useBillingSummary } from '../billing/useBillingSummary';
import { useLogoHop } from './useLogoHop';
import { WorkspaceMark } from '../workspaces/WorkspaceMark';
import { GetStartedDock } from '../tours/GetStartedDock';

/** Where the docs live. Not a setting: it is the same for every workspace. */
const DOCS_URL = 'https://docs.toado.dev/';

export interface TopBarProps {
  onOpenPalette?: () => void;
}

export function TopBar({ onOpenPalette }: TopBarProps) {
  const { theme, toggle } = useTheme();
  const { user, companies, setCurrentCompany, signOut } = useAuth();
  const currentCompany = useCurrentCompany();
  const navigate = useNavigate();
  const toast = useToast();
  const confirmNavigation = useNavigationConfirmation();
  const go = (destination: string) => {
    if (confirmNavigation()) navigate(destination);
  };

  const [coOpen, setCoOpen] = useState(false);
  const [createWorkspaceOpen, setCreateWorkspaceOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const coBtn = useRef<HTMLButtonElement>(null);
  const userBtn = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const brandRef = useRef<HTMLButtonElement>(null);
  useLogoHop(brandRef);

  useShortcut('/', (e) => { e.preventDefault(); searchRef.current?.focus(); }, { page: true });

  // Just enough billing to render the free-tier chip. The query key and the
  // staleness live in the hook now, which is also what keeps this and the
  // ticket page from fetching the same thing twice.
  const billing = useBillingSummary();
  const showFreeChip = billing?.plan === 'free'
    && typeof billing.ticketsUsedThisMonth === 'number'
    && typeof billing.ticketsCap === 'number';
  const remaining = showFreeChip ? Math.max(0, billing!.ticketsCap! - billing!.ticketsUsedThisMonth!) : 0;
  const usedRatio = showFreeChip ? billing!.ticketsUsedThisMonth! / billing!.ticketsCap! : 0;
  // Color thresholds: green/normal until 80%, yellow at 80–99%,
  // red at cap. Keeps the chip from being a constant attention-grab.
  const chipColor = remaining === 0 ? 'var(--danger)'
    : usedRatio >= 0.8 ? 'var(--warning)'
    : 'var(--text-secondary)';
  const chipBg = remaining === 0 ? 'var(--danger-bg, var(--bg-muted))'
    : usedRatio >= 0.8 ? 'var(--warning-bg, var(--bg-muted))'
    : 'var(--bg-muted)';

  // Dogfood: a header trigger for the Toado-on-Toado widget, wired to the
  // SDK's `window.Toado.open()`. Shown on exactly the same dev-or-staff gate
  // as DogfoodWidget, so the two are in lockstep: whenever the dogfood widget
  // is injected, this trigger is shown, and vice versa. Lets a staff user set
  // the widget's launcher to Hidden and report bugs from here instead of a
  // floating button. The Bug glyph inherits currentColor, so it renders in the
  // topbar's theme-aware ink (dark on light, light on dark) with no per-theme
  // asset.
  //
  // NOTE the gate tracks INJECTION, not a successful config load: in local dev
  // the baked prod key is fetched against the local API, which lacks that
  // widget, so the config 404s and this icon's click is a harmless no-op until
  // you set VITE_TOADO_WIDGET_KEY to a local widget. In prod (staff) it works.
  const showBugReport = import.meta.env.DEV || user?.isStaff === true;
  const openBugReport = (): void => {
    (window as unknown as { Toado?: { open?: () => void } }).Toado?.open?.();
  };

  return (
    <header className={styles.topbar}>
      <div className={styles.left}>
        <button
          ref={brandRef}
          type="button"
          onClick={() => go('/')}
          className={styles.brand}
          aria-label="Toado home"
          style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}
        >
          <Logo size={28} />
        </button>
        <button
          ref={coBtn}
          className={styles.coSwitch}
          onClick={() => setCoOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={coOpen}
        >
          {/* The name alone up here; the marks are in the menu (Jason, 22 Oct). */}
          <span className={styles.coCurrent} title={currentCompany?.name}>{currentCompany?.name ?? 'No company'}</span>
          <span className={styles.chev}>▾</span>
        </button>
        <Popover open={coOpen} onClose={() => setCoOpen(false)} anchor={coBtn.current?.getBoundingClientRect() ?? null}>
          <Menu className={styles.coMenu}>
            {companies.map((c) => (
              <MenuItem
                key={c.id}
                className={styles.coItem}
                leftIcon={<WorkspaceMark company={c} size={16} />}
                onClick={() => {
                  if (!confirmNavigation()) return;
                  setCurrentCompany(c.id);
                  setCoOpen(false);
                  navigate('/');
                }}
              >
                {/* A long name is cut short, with the whole of it on hover, and the
                    edit button keeps its place at the end (t-skZU6X). */}
                <span className={styles.coRow}>
                  <span className={styles.coName} title={c.name}>{c.name}</span>
                  {/* Straight to that workspace's settings, for its owners: the
                      settings page selects the workspace its link names. */}
                  {c.role === 'owner' && (
                    <Tooltip content="Workspace settings">
                      <button
                        type="button"
                        className={styles.coEdit}
                        aria-label={`Edit ${c.name} settings`}
                        data-testid="workspace-edit"
                        onClick={(event) => {
                          event.stopPropagation();
                          if (!confirmNavigation()) return;
                          setCoOpen(false);
                          navigate(`/company/settings/general?workspace=${encodeURIComponent(c.id)}`);
                        }}
                      >
                        <Pencil size={13} aria-hidden="true" />
                      </button>
                    </Tooltip>
                  )}
                </span>
              </MenuItem>
            ))}
            <MenuDivider />
            {/* A workspace for another business (TOADO-819). Company settings
                are in the sidebar, under Workspace. */}
            <MenuItem leftIcon={<Plus size={14} />} onClick={() => { setCoOpen(false); setCreateWorkspaceOpen(true); }}>
              Create new workspace
            </MenuItem>
          </Menu>
        </Popover>
        <CreateWorkspaceModal open={createWorkspaceOpen} onClose={() => setCreateWorkspaceOpen(false)} />
      </div>

      <div className={styles.center}>
        <form
          className={styles.search}
          onSubmit={(e) => {
            e.preventDefault();
            const q = searchRef.current?.value.trim() ?? '';
            if (q.length >= 2) go(`/search?q=${encodeURIComponent(q)}`);
          }}
        >
          <SearchIcon size={14} />
          <input
            ref={searchRef}
            className={styles.searchInput}
            placeholder="Search tickets…"
            type="search"
          />
          <span className={styles.kbd}>/</span>
        </form>
        {/* Beside search, its neighbour in purpose, rather than in the crowded
            row on the right (Jason, 28 Oct, TOADO-989). */}
        <Tooltip content="Command palette · ⌘K" placement="bottom">
          <button className={styles.iconBtn} aria-label="Command palette" onClick={onOpenPalette}>
            <Command size={16} />
          </button>
        </Tooltip>
      </div>

      <div className={styles.right}>
        <GetExtensionPrompt />
        {showFreeChip && (
          <Tooltip
            content={
              remaining === 0
                ? `You've used all ${billing!.ticketsCap} free tickets this month. Click to upgrade.`
                : `${billing!.ticketsUsedThisMonth} of ${billing!.ticketsCap} free tickets used this month. Click to upgrade.`
            }
            placement="bottom"
          >
            <button
              type="button"
              onClick={() => go('/company/settings/billing')}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '4px 10px',
                borderRadius: 999,
                border: `1px solid ${chipColor}`,
                background: chipBg,
                color: chipColor,
                fontSize: 12,
                fontWeight: 500,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
              aria-label="Free plan: ticket usage. Click to upgrade."
            >
              <span className={styles.wideOnly}>Free · </span>{remaining}/{billing!.ticketsCap} left<span className={styles.wideOnly}> this month</span>
            </button>
          </Tooltip>
        )}
        {/* An anchor rather than a button, because it leaves the app: that
            gets the middle-click, the open-in-new-tab and the status bar
            preview for free. `noreferrer` with `noopener` because the docs do
            not need to know which page somebody came from. */}
        <NotificationsBell />
        {/* The tours (TOADO-981): a tour or its offer shrinks into this as it goes. */}
        <GetStartedDock className={styles.iconBtn} />
        <Tooltip content="Help docs" placement="bottom">
          <a
            className={styles.iconBtn}
            href={DOCS_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Help docs, opens in a new tab"
          >
            <CircleHelp size={16} />
          </a>
        </Tooltip>
        {showBugReport && (
          <Tooltip content="Report a bug" placement="bottom">
            <button className={styles.iconBtn} aria-label="Report a bug" onClick={openBugReport}>
              <Bug size={16} />
            </button>
          </Tooltip>
        )}
        <button
          ref={userBtn}
          className={styles.userBtn}
          onClick={() => setUserOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={userOpen}
        >
          <Avatar name={user?.name} src={user?.avatarUrl} size="sm" />
        </button>
        <Popover
          open={userOpen}
          onClose={() => setUserOpen(false)}
          anchor={userBtn.current?.getBoundingClientRect() ?? null}
          placement="bottom-end"
        >
          <Menu>
            <MenuItem leftIcon={<UserIcon size={14} />} onClick={() => { setUserOpen(false); go('/me/settings'); }}>
              Profile
            </MenuItem>
            <MenuItem leftIcon={<Settings size={14} />} onClick={() => { setUserOpen(false); go('/me/settings/notifications'); }}>
              Settings
            </MenuItem>
            {/* The theme lives with the person's own settings now (TOADO-989).
                Named by what it does, so the menu says where it goes. */}
            <MenuItem
              leftIcon={theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
              onClick={() => { toggle(); setUserOpen(false); }}
              data-testid="theme-toggle"
            >
              {theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            </MenuItem>
            {/* Toado-staff-only entry into the admin panel. The
                `/me` endpoint only returns `isStaff: true` for actual
                staff (omits the field otherwise), so the strict
                `=== true` check keeps the menu item invisible to
                everyone else AND silent if the field ever changes
                shape. Server-side gate is the authoritative one;
                this is purely UX. */}
            {user?.isStaff === true && (
              <MenuItem leftIcon={<ShieldCheck size={14} />} onClick={() => { setUserOpen(false); go('/admin'); }}>
                Admin
              </MenuItem>
            )}
            <MenuDivider />
            <MenuItem data-testid="sweep-skip" leftIcon={<LogOut size={14} />} destructive onClick={async () => {
              // Asked here, before anything ends: sign-out leaves the page
              // without asking again.
              if (!confirmNavigation()) return;
              setUserOpen(false);
              try {
                await signOut();
              } catch {
                toast.push({
                  variant: 'danger',
                  title: 'Sign-out failed',
                  description: 'You are still signed in. Try again.',
                });
              }
            }}>
              Sign out
            </MenuItem>
          </Menu>
        </Popover>
      </div>
    </header>
  );
}
